import { type SxProps, type Theme } from '@mui/material/styles';
import { type DataGridProProps, type GridColDef, type GridDensity, type GridRowId, type GridRowModel, type GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { type ReactNode } from 'react';
import { type IconifyProps } from '../../components/iconify/types';
import { type InputCheckProps } from '../inputfields/Check';
import { type InputDateTimeProps } from '../inputfields/DateTime';
import { type InputImageProps } from '../inputfields/Image';
import { type InputNumberProps } from '../inputfields/Number';
import { type InputPasswordProps } from '../inputfields/Password';
import { type InputRatingProps } from '../inputfields/Rating';
import { type InputSelectProps, type SelectDataObject } from '../inputfields/Select';
import { type InputSliderProps } from '../inputfields/Slider';
import { type InputTextProps } from '../inputfields/Text';

export enum ColumnWidths {
    xxs = 50,
    xs = 75,
    s = 100,
    m = 170,
    l = 220,
    xl = 280,
    xxl = 440,
}

type Action = {
    buttonName: string;
    func: () => void;
    icon: IconifyProps;
};

type RowAction = {
    buttonName: string | ((rowId: GridRowId, row: GridRowModel) => string);
    disabled: boolean | ((rowId: GridRowId, row: GridRowModel) => boolean);
    func: (rowId: GridRowId, row: GridRowModel) => void;
    hidden: boolean | ((rowId: GridRowId, row: GridRowModel) => boolean);
    icon: IconifyProps | ((rowId: GridRowId, row: GridRowModel) => IconifyProps);
    iconColor?: string | ((rowId: GridRowId, row: GridRowModel) => string);
};

type ToolbarAction = {
    buttonName: string;
    func: (rowIds: GridRowId[]) => void;
    icon: IconifyProps;
};

export type DataGridDetail = {
    content: DataGridEnrichedColDef[];
    gridItemProps: {
        xs: number | false;
        sm?: number | false;
        md?: number | false;
        lg?: number | false;
        xl?: number | false;
        sx?: SxProps<Theme>;
    };
};

export type Filter = (InputCheckProps | InputDateTimeProps | InputImageProps | InputNumberProps | InputPasswordProps | InputRatingProps | InputSelectProps | InputSliderProps | InputTextProps) & {
    advanced?: boolean;
    delay?: number;
    divider?: boolean;
    endAdvanced?: boolean;
    gridItemProps: {
        xs: number | [number, number];
        sm?: number | [number, number];
        md?: number | [number, number];
        lg?: number | [number, number];
        xl?: number | [number, number];
    };
    startAdvanced?: boolean;
    spacer?: boolean;
};

export type Search = {
    [index: Filter['id']]: Filter['v'];
};

export type Sort = {
    field: string;
    direction: 'asc' | 'desc';
};

type AdditionalColFields = (
    | InputCheckProps
    | InputDateTimeProps
    | InputImageProps
    | InputNumberProps
    | InputPasswordProps
    | InputRatingProps
    | InputSelectProps
    | InputSliderProps
    | InputTextProps
    | { type: 'actions' }
) & {
    addable?: boolean;
    defaultSort?: 'asc' | 'desc';
    disableCellClick?: boolean;
    disableEditableCell?: (row: GridRowModel) => boolean;
    editableBulk?: boolean;
    forceEdit?: (props: { id: GridRowId; field: string; value: any }) => void;
    group?: string;
    groupSort?: string;
    hidden?: boolean;
    hideOnDensityCompact?: boolean;
    iconHeaderName?: ReactNode;
    id?: string;
    isMultiline?: boolean;
    noCSV?: boolean;
    order?: number;
    pinned?: 'left' | 'right' | false;
    show?: boolean;
    sort?: 'asc' | 'desc' | false;
};

export type DataGridEnrichedColDef = Omit<GridColDef, 'type'> & AdditionalColFields;

export type DataGridProps = {
    actionColumnHeaderName?: string;
    bulkActions?: Record<
        'noMenu' | string,
        {
            name: string;
            icon: IconifyProps;
            actions: ToolbarAction[];
        }
    >;
    columns: DataGridEnrichedColDef[];
    columnActions?: RowAction[];
    debug?: boolean;
    density?: GridDensity;
    details?: (row: GridRowModel) => DataGridDetail[];
    detailsHeight?: number;
    disableColumnMenu?: boolean;
    disableExport?: boolean;
    disableHeader?: boolean;
    disableLayout?: boolean;
    disableSorting?: boolean;
    disableToolbar?: boolean;
    filterData?: {
        suche?: Search;
        sort?: Sort;
    };
    filters?: Filter[];
    height?: number;
    layouts?: SelectDataObject[];
    layoutValue?: SelectDataObject['i'];
    onColumnOrderChange?: (newColumns: DataGridEnrichedColDef[]) => void;
    onColumnVisibilityModelChange?: (newColumns: DataGridEnrichedColDef[]) => void;
    onColumnWidthChange?: (newColumns: DataGridEnrichedColDef[]) => void;
    onDensityChange?: (newDensity: GridDensity) => void;
    onExportPdf?: (params: {
        cols: { field: DataGridEnrichedColDef['field']; headerName: DataGridEnrichedColDef['headerName']; type: DataGridEnrichedColDef['type'] }[];
        data: { [index: string]: string }[];
    }) => void;
    onLayoutChange?: (id: SelectDataObject['i']) => void;
    onFilter?: (suche: Search, sort: Sort) => void;
    onFilterReset?: () => void;
    onPinnedChanged?: (newColumns: DataGridEnrichedColDef[]) => void;
    onResetLayout?: (tableID: SelectDataObject['i']) => void;
    onRowAdd?: (newRow: GridRowModel) => void;
    onRowBulkDelete?: (rowIds: GridRowId[], oldRows: GridRowModel[]) => void;
    onRowBulkEdit?: (rowIds: GridRowId[], newRow: GridRowModel, oldRows: GridRowModel[]) => void;
    onRowClick?: (rowId: GridRowId, row: GridRowModel) => void;
    onRowDelete?: (rowId: GridRowId, row: GridRowModel) => (rowId: GridRowId, oldRow: GridRowModel) => void;
    onRowEdit?: (rowId: GridRowId, row: GridRowModel) => (rowId: GridRowId, newRow: GridRowModel, oldRow: GridRowModel) => Promise<boolean>;
    onSearch?: (search: string) => void;
    onSelectionChange?: (selection: GridRowSelectionModel) => void;
    onSortChange?: (newSortedIds: GridRowId[]) => void;
    onSortModelChange?: (newColumns: DataGridEnrichedColDef[]) => void;
    pagination?: boolean;
    loading?: DataGridProProps['loading'];
    noResultsOverlay?: {
        text: string;
        icon: IconifyProps;
        func: () => void;
    }[];
    noRowsOverlay?: {
        text: string;
        icon: IconifyProps;
        func: () => void;
    }[];
    rows: DataGridProProps['rows'];
    rowsPerPage?: number;
    rowsPerPageOptions?: number[];
    selected?: GridRowSelectionModel;
    toolbarActions?: Action[];
    toolbarElements?: ReactNode;
    toolbarTitle?: {
        primary?: ReactNode;
        secondary?: ReactNode;
    };
    uniqueRowIdentifier: GridRowId;
    useInternSearch?: boolean;
    validation?: Record<GridRowId, (value: any, row: GridRowModel) => string | undefined>;
};
